import React, {useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useIntl} from 'react-intl';
import {Formik} from 'formik';
import {isEqual} from 'lodash';
import moment from 'moment';
import {useInvitedUsersUIContext} from '../InvitedUsersUIContext';
import * as actions from '../../_redux/invitedUsersActions';
import ButtonExportToExcel from '../../../_common/components/ButtonExportToExcel';
import {FilterFromDate, FilterToDate} from '../../../_common/components/FilterDateRange';
import {generateExcelFile} from '../../../_common/utils/excel';
import {formatPhoneNumberForExcel} from '../../../../../utils';
import {invitedUsersHeader} from '../../../_common/constants/exportedFilesMainHeaders';
import {headersInvitedUsersExcelFile} from '../../../_common/constants/exportedFilesHeaders';
import {getLastCompletedStepLabel} from '../../../_common/utils/getLastCompletedStepLabel';
import {RequestCancellationManager} from '../../../_common/utils/RequestCancellationManager';
import FiltersBar from '../../../_common/components/FiltersBar';

const prepareFilter = (queryParams, values) => {
  const {fromDate, toDate} = values;
  const newQueryParams = {...queryParams};
  const filter = {};
  if (Boolean(fromDate)) {
    filter.fromDate = moment(fromDate).format('YYYY-MM-DD');
  } else {
    delete newQueryParams.fromDate;
  }
  if (Boolean(toDate)) {
    filter.toDate = moment(toDate).add(1, 'days').format('YYYY-MM-DD');
  } else {
    delete newQueryParams.toDate;
  }

  return {...newQueryParams, ...filter};
};

export function InvitedUsersFilter() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const invitedUsersUIContext = useInvitedUsersUIContext();
  const invitedUsersUIProps = useMemo(() => {
    return {
      queryParams: invitedUsersUIContext.queryParams,
      setQueryParams: invitedUsersUIContext.setQueryParams,
    };
  }, [invitedUsersUIContext]);
  const [loading, setLoading] = useState(false);

  const applyFilter = (values) => {
    RequestCancellationManager.stopRequests(actions.getInvitedUsersListIDPrefix);

    const newQueryParams = prepareFilter(invitedUsersUIProps.queryParams, values);
    if (!isEqual(newQueryParams, invitedUsersUIProps.queryParams)) {
      invitedUsersUIProps.setQueryParams(newQueryParams);
    }
  };

  const qs = new URLSearchParams(window.location.search);
  const fromDate = qs.get('fromDate') || '';
  const toDate = Boolean(qs.get('toDate'))
    ? moment(qs.get('toDate')).subtract(1, 'days').toDate()
    : '';

  const exportInvitedUsersFileFunc = () => {
    setLoading(true);
    dispatch(actions.exportInvitedUsersFileAction(invitedUsersUIProps.queryParams))
      .then(prepareDataForExcel)
      .then((data) => {generateExcelFile(data, 'Invite friend.xlsx', invitedUsersHeader);})
      .catch((error) => {
        console.error(`Error: + ${error}`);
        setLoading(false);
      })
      .finally(() => setLoading(false));
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        invitedFriend,
        invitingUser,
        lastCompletedStep,
        invoices,
      } = item;

      return {
        [headersInvitedUsersExcelFile.itemNumber]: index + 1,
        [headersInvitedUsersExcelFile.invitingUserName]: invitingUser.name,
        [headersInvitedUsersExcelFile.invitingUserAgentID]: invitingUser.agentID,
        [headersInvitedUsersExcelFile.invitedFriendName]: invitedFriend.fullName,
        [headersInvitedUsersExcelFile.invitedFriendPhone]: formatPhoneNumberForExcel(invitedFriend.phone),
        [headersInvitedUsersExcelFile.lastCompletedStep]: getLastCompletedStepLabel(lastCompletedStep),
        [headersInvitedUsersExcelFile.agentID]: invitedFriend.agentId,
        [headersInvitedUsersExcelFile.registrationDate]: moment(invitedFriend.createdAt).format('DD.MM.YYYY'),
        [headersInvitedUsersExcelFile.invoicesCount]: invoices.count,
        [headersInvitedUsersExcelFile.invoicesTotalAmount]: invoices.total,
      };
    });
  };

  const containerClassnames = 'w-200px position-relative my-1 px-3';

  return (
    <Formik
      initialValues={{fromDate, toDate}}
      onSubmit={(values) => applyFilter(values)}
      enableReinitialize={true}
    >
      {({values, ...formikProps}) => (
        <FiltersBar
          filters={
            <div className={'d-flex flex-wrap justify-content-center align-items-center mb-0 w-100'}>
              <FilterFromDate
                values={values}
                placeholder={intl.formatMessage({id: 'invited-users-filter-fromDate', defaultMessage: 'From date'})}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterToDate
                values={values}
                placeholder={intl.formatMessage({id: 'invited-users-filter-toDate', defaultMessage: 'To date'})}
                containerStyles={containerClassnames}
                {...formikProps}
              />
            </div>
          }
          buttons={
            <ButtonExportToExcel
              loading={loading}
              exportFunc={exportInvitedUsersFileFunc}
            />
          }
        />
      )}
    </Formik>
  );
}
